// Program Edit Impact Review (docs/program-edit-impact-design.html §8.1):
// shared requirement-vs-program resolution check, mirroring the server-side
// guard (MeetProgramImpactService.RequirementResolves). A requirement resolves
// when its group link matches a program group — by the group's display name
// ("U8 M" for gender-split age groups), falling back to the raw group name —
// and, for event-mapped roles, at least one of the role's event codes is
// present. The TRACK_EVENTS_CODE sentinel (900) in a mapping matches any
// event whose club EventType category is Track or Training. General roles
// always resolve; meet-wide event-official roles resolve against any event
// anywhere in the program.

import {
  TRACK_EVENTS_CODE,
  TRACK_EVENT_CATEGORIES,
} from 'constants/eventTypes';

const toEventCodeArray = (eventTypeMapping) => {
  if (!eventTypeMapping) return [];
  const parts = Array.isArray(eventTypeMapping)
    ? eventTypeMapping
    : String(eventTypeMapping).split(',');
  return parts
    .map((part) =>
      typeof part === 'number' ? part : parseInt(String(part).trim(), 10)
    )
    .filter((code) => !Number.isNaN(code));
};

const groupDisplayName = (group) =>
  group.displayName ||
  (group.groupSource === 'AgeGroup' && group.gender && group.gender !== 'Mixed'
    ? `${group.groupName} ${group.gender}`
    : group.groupName);

const groupEventCodes = (group) =>
  (group.events || [])
    .map((event) =>
      typeof event.eventType === 'string'
        ? parseInt(event.eventType, 10)
        : event.eventType
    )
    .filter((code) => code !== null && code !== undefined && !Number.isNaN(code));

/**
 * Event codes the club classifies as track events (category Track or
 * Training) — the set the TRACK_EVENTS_CODE sentinel expands to.
 */
export const getTrackEventCodes = (clubEventTypes) =>
  new Set(
    (clubEventTypes || [])
      .filter((eventType) =>
        TRACK_EVENT_CATEGORIES.includes(eventType.category)
      )
      .map((eventType) => eventType.eventCode)
  );

/**
 * Does a role's event mapping match any of the given event codes? The
 * TRACK_EVENTS_CODE sentinel matches when any of the events is a track
 * event; real codes match literally.
 */
export const mappingMatchesEvents = (mapping, eventCodes, trackEventCodes) =>
  mapping.some((code) =>
    code === TRACK_EVENTS_CODE
      ? eventCodes.some((eventCode) => trackEventCodes.has(eventCode))
      : eventCodes.includes(code)
  );

export const requirementResolvesToProgram = (
  requirement,
  role,
  programGroups,
  clubEventTypes
) => {
  const groups = programGroups || [];
  const mapping = toEventCodeArray(role?.eventTypeMapping);
  const trackEventCodes = getTrackEventCodes(clubEventTypes);

  if (requirement.programGroupName && requirement.groupType) {
    const sameType = groups.filter(
      (group) => (group.groupSource || group.groupType) === requirement.groupType
    );
    const group =
      sameType.find((g) => groupDisplayName(g) === requirement.programGroupName) ||
      sameType.find((g) => g.groupName === requirement.programGroupName);
    if (!group) return false;
    if (mapping.length === 0) return true;
    return mappingMatchesEvents(mapping, groupEventCodes(group), trackEventCodes);
  }

  const roleCategory = role?.roleCategory || requirement.roleCategory;
  if (roleCategory === 'EventSpecific' && mapping.length > 0) {
    const allEvents = groups.flatMap(groupEventCodes);
    return mappingMatchesEvents(mapping, allEvents, trackEventCodes);
  }

  return true; // General / unmapped roles are not program-linked
};
